import React, { useState, useEffect } from 'react';
import { productApi, stripeApi } from '../services/api';
import { Lock, Check, Loader2, Plane, AlertCircle, X } from 'lucide-react';

const PaywallModal = (props) => {
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');

  // Dynamic pricing state
  const [stripePrice, setStripePrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchProduct();
      fetchStripePrice();
    }
  }, [isOpen]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      await productApi.get('core');
    } catch (err) {
      console.error('Failed to fetch product:', err);
      setError('Failed to load product information');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripePrice = async () => {
    try {
      setPriceLoading(true);
      const { data } = await stripeApi.getPrice();
      setStripePrice(data);
    } catch (err) {
      console.error('Failed to fetch Stripe price:', err);
      // Non-fatal - will fall back to product price
    } finally {
      setPriceLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setCheckoutLoading(true);
      setError('');

      const originUrl = window.location.origin;
      // Backend fetches price directly from Stripe - we don't send price_id
      const { data } = await stripeApi.createCheckoutSession('core', originUrl);

      // Full page redirect - handle iframe case
      if (window.top !== window.self) {
        window.top.location.href = data.url;
      } else {
        window.location.href = data.url;
      }
      return;
    } catch (err) {
      console.error('Checkout error:', err);
      const message = err.response?.data?.detail || 'Failed to start checkout';
      setError(message);
      setCheckoutLoading(false);
    }
  };

  // Get display price from Stripe (source of truth) - NO FALLBACKS. No space after the currency symbol.
  const displayPrice = stripePrice
    ? `${stripePrice.currency === 'GBP' ? '£' : stripePrice.currency + ' '}${stripePrice.price.toFixed(2)}`
    : null; // Show loading spinner instead of fallback

  if (!isOpen) return null;

  // Static features for consistent display - text unchanged
  const features = [
    "All 21 phases, Pre-Flight to Go Around, in correct flow order",
    "Full MCDU setup tutorial, step by step",
    "Explanatory notes where the \"why\" matters",
    "In-Flight Assistance: ECAM fault lookup with quick rectification steps",
    "One-time payment. Lifetime access, no subscription."
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="paywall-modal">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Card */}
      <div
        className="relative w-full max-w-lg rounded-gc-card border shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}
      >
        {/* Header */}
        <div className="relative px-8 pt-10 pb-6 text-center">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 border"
            style={{ background: 'var(--gc-current-row)', borderColor: 'var(--gc-cyan)' }}
          >
            <Plane className="w-8 h-8" style={{ color: 'var(--gc-cyan)' }} />
          </div>

          <div className="space-y-1.5">
            <h2 className="font-barlow-condensed font-bold text-3xl" style={{ color: 'var(--gc-text-main)' }}>
              Unlock Flight Sim Academy
            </h2>
            <p className="font-plex-mono text-xs font-medium tracking-[0.15em] uppercase" style={{ color: 'var(--gc-amber)' }}>
              Fenix A320 Edition
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--gc-cyan)' }} />
            </div>
          ) : (
            <>
              {/* Features */}
              <div className="rounded-gc-card border overflow-hidden mb-6" style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-checklist-card)' }}>
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 px-4 py-3 border-b last:border-0"
                    style={{ borderColor: 'var(--gc-border-row)' }}
                  >
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--gc-green)' }} />
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="rounded-gc-card border p-5 mb-6 text-center" style={{ borderColor: 'var(--gc-border-card)' }}>
                {priceLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--gc-cyan)' }} />
                ) : (
                  <span className="font-barlow-condensed font-bold text-3xl" style={{ color: 'var(--gc-text-main)' }}>
                    {displayPrice} one-time
                  </span>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="mb-6 p-4 rounded-gc-card border flex items-center gap-3" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.4)' }}>
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handlePurchase}
                disabled={checkoutLoading}
                className="w-full font-barlow-condensed font-bold text-lg h-14 rounded-gc-button transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
                data-testid="unlock-workflow-btn"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirecting to checkout...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Unlock Full Access
                  </>
                )}
              </button>

              <p className="text-xs text-center mt-4 leading-relaxed" style={{ color: 'var(--gc-text-faint)' }}>
                Secure checkout by Stripe. Built for the Fenix A320. Asobo and other A320 variants can follow the flows, but EFB steps will differ.
              </p>
            </>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-gc-button transition-colors"
          style={{ color: 'var(--gc-text-muted)' }}
          aria-label="Close"
          data-testid="paywall-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default PaywallModal;
